import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input, Select } from "@/components/ui/form";
import { Button, buttonVariants } from "@/components/ui/button";
import { Badge, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/data";
import { Sheet } from "@/components/ui/overlays";
import { api } from "@/lib/api";
import { useToast } from "@/app/Toast";
import { cn, formatDateTime, formatMoney } from "@/lib/utils";
import {
  CalendarDays, ChevronLeft, ChevronRight, Clock3, List, MapPin,
  RefreshCw, Search, UserRound, Wrench,
} from "lucide-react";

interface BookingRow {
  id: number;
  code: string;
  status: string;
  startAt: string;
  endAt: string;
  version: number;
  customer: { id: number; name: string; phone: string | null };
  vehicle: { id: number; plate: string } | null;
  resource: { id: number; name: string; type: string };
  resources: Array<{ id: number; name: string; type: string; role: "PRIMARY" | "STAFF" }>;
  location: { id: number; name: string };
  serviceMode: ServiceMode;
  total: { amount: number; currency: string };
}

interface LocationRow { id: number; name: string; timezone: string; currency: string; }
interface ResourceRow { id: number; name: string; type: string; locationId: number; active?: boolean; }
interface HoursRow { id: number; dayOfWeek: number; sequence: number; openTime: string; closeTime: string; isClosed: boolean; }
interface BlackoutRow { id: number; locationId: number; resourceId: number | null; startsAtUtc: string; endsAtUtc: string; reason: string | null; }
type ViewMode = "day" | "week" | "list";
type ServiceMode = "STATIONARY" | "MOBILE";

const STATUSES = ["PENDING", "CONFIRMED", "CHECKED_IN", "IN_PROGRESS", "COMPLETED", "CANCELLED", "NO_SHOW"];
const SLOT_MINUTES = 30;
const SLOT_HEIGHT = 24;
const LOCATION_KEY = "scheduler-location-id";

function zonedTime(value: Date | string, timeZone: string) {
  const date = typeof value === "string" ? new Date(value) : value;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? "0";
  return {
    localDate: `${part("year")}-${part("month")}-${part("day")}`,
    hour: Number(part("hour")) % 24,
    minute: Number(part("minute")),
  };
}

function zonedLocalToUtc(date: string, time: string, timeZone: string): Date {
  const [year = 1970, month = 1, day = 1] = date.split("-").map(Number);
  const [hour = 0, minute = 0] = time.split(":").map(Number);
  const desired = Date.UTC(year, month - 1, day, hour, minute);
  let guess = desired;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const actual = zonedTime(new Date(guess), timeZone);
    const actualEpoch = Date.UTC(
      Number(actual.localDate.slice(0, 4)),
      Number(actual.localDate.slice(5, 7)) - 1,
      Number(actual.localDate.slice(8, 10)),
      actual.hour,
      actual.minute,
    );
    guess += desired - actualEpoch;
  }
  return new Date(guess);
}

function todayInZone(timeZone: string): string {
  return zonedTime(new Date(), timeZone).localDate;
}

function addDays(date: string, amount: number): string {
  const value = new Date(`${date}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() + amount);
  return value.toISOString().slice(0, 10);
}

function startOfWeek(date: string): string {
  const day = new Date(`${date}T00:00:00Z`).getUTCDay();
  return addDays(date, day === 0 ? -6 : 1 - day);
}

function dayOfWeek(date: string): number {
  return new Date(`${date}T00:00:00Z`).getUTCDay();
}

function minutes(time: string): number {
  const [hour = 0, minute = 0] = time.slice(0, 5).split(":").map(Number);
  return hour * 60 + minute;
}

function timeLabel(totalMinutes: number): string {
  const hour = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function modeOf(booking: BookingRow): ServiceMode {
  return booking.serviceMode ?? (booking.resource.type === "STAFF_MEMBER" ? "MOBILE" : "STATIONARY");
}

function assignmentsOf(booking: BookingRow) {
  return booking.resources.length ? booking.resources : [{ ...booking.resource, role: "PRIMARY" as const }];
}

function formatDay(date: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(undefined, { ...options, timeZone: "UTC" }).format(new Date(`${date}T12:00:00Z`));
}

function normalizeHours(rows: Array<HoursRow & Record<string, unknown>>): HoursRow[] {
  return rows.map((row) => ({
    id: Number(row.id),
    dayOfWeek: Number(row.dayOfWeek ?? row.day_of_week),
    sequence: Number(row.sequence),
    openTime: String(row.openTime ?? row.open_time),
    closeTime: String(row.closeTime ?? row.close_time),
    isClosed: Boolean(row.isClosed ?? row.is_closed),
  }));
}

function normalizeBlackouts(rows: Array<BlackoutRow & Record<string, unknown>>): BlackoutRow[] {
  return rows.map((row) => ({
    id: Number(row.id),
    locationId: Number(row.locationId ?? row.location_id),
    resourceId: row.resourceId != null || row.resource_id != null ? Number(row.resourceId ?? row.resource_id) : null,
    startsAtUtc: new Date(String(row.startsAtUtc ?? row.starts_at_utc)).toISOString(),
    endsAtUtc: new Date(String(row.endsAtUtc ?? row.ends_at_utc)).toISOString(),
    reason: (row.reason as string | null) ?? null,
  }));
}

async function fetchAllBookings(params: URLSearchParams, signal: AbortSignal): Promise<BookingRow[]> {
  const all: BookingRow[] = [];
  let offset = 0;
  while (true) {
    const pageParams = new URLSearchParams(params);
    pageParams.set("limit", "200");
    pageParams.set("offset", String(offset));
    const page = await api.get<BookingRow[]>(`/api/v1/bookings?${pageParams}`, signal);
    all.push(...page);
    if (page.length < 200) return all;
    offset += page.length;
  }
}

export function BookingsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [locationId, setLocationId] = useState<number | null>(null);
  const [resources, setResources] = useState<ResourceRow[]>([]);
  const [hours, setHours] = useState<HoursRow[]>([]);
  const [blackouts, setBlackouts] = useState<BlackoutRow[]>([]);
  const [rows, setRows] = useState<BookingRow[]>([]);
  const [view, setView] = useState<ViewMode>("day");
  const [day, setDay] = useState("");
  const [resourceId, setResourceId] = useState("");
  const [status, setStatus] = useState("");
  const [mode, setMode] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [preview, setPreview] = useState<BookingRow | null>(null);
  const [reschedulingId, setReschedulingId] = useState<number | null>(null);

  const location = locations.find((item) => item.id === locationId) ?? null;
  const timeZone = location?.timezone ?? "UTC";
  const weekStart = day ? startOfWeek(day) : "";
  const window = useMemo(() => {
    if (!day) return null;
    if (view === "day") return { from: day, to: addDays(day, 1) };
    if (view === "week") return { from: weekStart, to: addDays(weekStart, 7) };
    return { from: day, to: addDays(day, 14) };
  }, [day, view, weekStart]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    api.get<LocationRow[]>("/api/v1/locations", controller.signal)
      .then((items) => {
        setLocations(items);
        const stored = Number(sessionStorage.getItem(LOCATION_KEY));
        const selected = items.find((item) => item.id === stored) ?? items.find(() => true) ?? null;
        setLocationId(selected?.id ?? null);
        if (selected) setDay(todayInZone(selected.timezone));
        if (!selected) setError("No locations are available.");
      })
      .catch((reason) => {
        if (reason.name !== "AbortError") setError(reason.message);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!locationId) return;
    const controller = new AbortController();
    setError(null);
    Promise.all([
      api.get<ResourceRow[]>(`/api/v1/ops/resources?locationId=${locationId}`, controller.signal),
      api.get<Array<HoursRow & Record<string, unknown>>>(`/api/v1/ops/business-hours?locationId=${locationId}`, controller.signal),
    ]).then(([resourceRows, hourRows]) => {
      setResources(resourceRows);
      setHours(normalizeHours(hourRows));
    }).catch((reason) => {
      if (reason.name !== "AbortError") setError(reason.message);
    });
    return () => controller.abort();
  }, [locationId, refreshKey]);

  useEffect(() => {
    if (!locationId || !window) return;
    const controller = new AbortController();
    const params = new URLSearchParams({
      locationId: String(locationId),
      from: window.from,
      to: window.to,
    });
    if (status) params.set("status", status);
    if (resourceId) params.set("resourceId", resourceId);
    const blackoutParams = new URLSearchParams({
      locationId: String(locationId),
      from: zonedLocalToUtc(window.from, "00:00", timeZone).toISOString(),
      to: zonedLocalToUtc(window.to, "00:00", timeZone).toISOString(),
    });
    setLoading(true);
    setError(null);
    Promise.all([
      fetchAllBookings(params, controller.signal),
      api.get<Array<BlackoutRow & Record<string, unknown>>>(`/api/v1/ops/blackouts?${blackoutParams}`, controller.signal),
    ]).then(([bookingRows, blackoutRows]) => {
      setRows(bookingRows);
      setBlackouts(normalizeBlackouts(blackoutRows));
    }).catch((reason) => {
      if (reason.name !== "AbortError") setError(reason.message);
    }).finally(() => setLoading(false));
    return () => controller.abort();
  }, [locationId, resourceId, status, timeZone, window, refreshKey]);

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    return rows.filter((booking) => {
      if (mode && modeOf(booking) !== mode) return false;
      if (!needle) return true;
      return [booking.code, booking.customer.name, booking.customer.phone ?? "", booking.vehicle?.plate ?? "", ...assignmentsOf(booking).map((item) => item.name)]
        .some((value) => value.toLocaleLowerCase().includes(needle));
    });
  }, [rows, mode, search]);

  function selectLocation(nextId: number) {
    const next = locations.find((item) => item.id === nextId);
    setLocationId(nextId);
    sessionStorage.setItem(LOCATION_KEY, String(nextId));
    setResourceId("");
    if (next) setDay(todayInZone(next.timezone));
  }

  function moveDate(direction: number) {
    const increment = view === "week" ? 7 : view === "list" ? 14 : 1;
    setDay((current) => addDays(current, direction * increment));
  }

  function newBooking(date: string, time: string, targetResourceId?: number) {
    if (!locationId) return;
    const params = new URLSearchParams({ locationId: String(locationId), date, time });
    if (targetResourceId) params.set("resourceId", String(targetResourceId));
    navigate(`/bookings/new?${params}`);
  }

  async function reschedule(booking: BookingRow, date: string, time: string, targetResourceId: number) {
    if (reschedulingId) return;
    const target = resources.find((resource) => resource.id === targetResourceId);
    const bookingMode = modeOf(booking);
    if (!target || (bookingMode === "MOBILE") !== (target.type === "STAFF_MEMBER")) {
      toast("error", `A ${bookingMode.toLowerCase()} booking cannot be moved to that resource.`);
      return;
    }
    const staffResourceIds = assignmentsOf(booking)
      .filter((resource) => resource.role === "STAFF" && resource.id !== targetResourceId)
      .map((resource) => resource.id);
    setReschedulingId(booking.id);
    try {
      await api.post(`/api/v1/bookings/${booking.id}/reschedule`, {
        resourceId: targetResourceId,
        staffResourceIds,
        startAt: zonedLocalToUtc(date, time, timeZone).toISOString(),
        version: booking.version,
      });
      toast("success", `${booking.code} moved to ${formatDay(date, { month: "short", day: "numeric" })} at ${time}.`);
      setPreview(null);
      setRefreshKey((value) => value + 1);
    } catch (reason) {
      const apiError = reason as Error & { code?: string; status?: number };
      const conflict = apiError.status === 409 || apiError.code === "SLOT_TAKEN" || apiError.code === "VERSION_CONFLICT";
      toast("error", conflict
        ? "That slot is no longer available or the booking changed. The calendar has been refreshed."
        : apiError.message || "Could not reschedule the booking.", 6500);
      setRefreshKey((value) => value + 1);
    } finally {
      setReschedulingId(null);
    }
  }

  async function transition(booking: BookingRow, status: "CHECKED_IN" | "IN_PROGRESS" | "COMPLETED" | "NO_SHOW") {
    try {
      await api.post(`/api/v1/bookings/${booking.id}/status`, { status, version: booking.version });
      toast("success", `${booking.code} marked ${status.toLowerCase().replaceAll("_", " ")}.`);
      setPreview(null);
      setRefreshKey((value) => value + 1);
    } catch (reason) {
      toast("error", reason instanceof Error ? reason.message : "Could not update booking status");
      setRefreshKey((value) => value + 1);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Bookings</h1>
          {location && <p className="mt-0.5 text-sm text-muted-foreground">{location.name} · {timeZone}</p>}
        </div>
        <Link to="/bookings/new" className={buttonVariants()}>New booking</Link>
      </div>

      <Card>
        <CardHeader className="space-y-3 border-b">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-[minmax(150px,1fr)_minmax(150px,1fr)_minmax(140px,1fr)_minmax(140px,1fr)_minmax(180px,1.4fr)]">
            <Select aria-label="Location" value={locationId?.toString() ?? ""} onChange={(event) => selectLocation(Number(event.target.value))}>
              {locations.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </Select>
            <Select aria-label="Resource" value={resourceId} onChange={(event) => setResourceId(event.target.value)}>
              <option value="">All resources</option>
              {resources.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </Select>
            <Select aria-label="Status" value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">All statuses</option>
              {STATUSES.map((item) => <option key={item} value={item}>{item.replaceAll("_", " ")}</option>)}
            </Select>
            <Select aria-label="Mode" value={mode} onChange={(event) => setMode(event.target.value)}>
              <option value="">All modes</option>
              <option value="STATIONARY">Stationary</option>
              <option value="MOBILE">Mobile</option>
            </Select>
            <label className="relative">
              <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input aria-label="Search bookings" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search customer, plate, code…" className="pl-9" />
            </label>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" onClick={() => moveDate(-1)} aria-label="Previous period"><ChevronLeft className="h-4 w-4" /></Button>
              <Input type="date" value={day} onChange={(event) => setDay(event.target.value)} className="h-8 w-36 text-xs" />
              <Button size="sm" variant="outline" onClick={() => moveDate(1)} aria-label="Next period"><ChevronRight className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => setDay(todayInZone(timeZone))}>Today</Button>
            </div>
            <div className="flex items-center gap-1">
              {(["day", "week", "list"] as const).map((item) => (
                <Button key={item} size="sm" variant={view === item ? "secondary" : "ghost"} onClick={() => setView(item)}>
                  {item === "day" ? <CalendarDays className="h-3.5 w-3.5" /> : item === "week" ? <Clock3 className="h-3.5 w-3.5" /> : <List className="h-3.5 w-3.5" />}
                  <span className="capitalize">{item}</span>
                </Button>
              ))}
              <Button size="sm" variant="outline" onClick={() => setRefreshKey((value) => value + 1)} disabled={loading}>
                <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} /> Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-5 md:pt-6">
          {error && <div role="alert" className="mb-4 rounded-lg border border-destructive/25 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">{error}</div>}
          {loading && rows.length === 0 ? (
            <div className="grid min-h-56 place-items-center text-sm text-muted-foreground"><RefreshCw className="mr-2 inline h-4 w-4 animate-spin" />Loading bookings…</div>
          ) : (
            <>
              <div className="hidden md:block">
                {view === "day" && day && (
                  <DayGrid day={day} bookings={filteredRows} resources={resources} selectedResourceId={resourceId} hours={hours} blackouts={blackouts} timeZone={timeZone} reschedulingId={reschedulingId} onOpen={setPreview} onNew={newBooking} onReschedule={reschedule} />
                )}
                {view === "week" && day && (
                  <WeekView start={weekStart} bookings={filteredRows} resources={resources} hours={hours} blackouts={blackouts} timeZone={timeZone} reschedulingId={reschedulingId} onOpen={setPreview} onNew={newBooking} onReschedule={reschedule} />
                )}
                {view === "list" && <ListView bookings={filteredRows} timeZone={timeZone} onOpen={setPreview} />}
              </div>
              <div className="md:hidden">
                <Agenda bookings={filteredRows} timeZone={timeZone} onOpen={setPreview} />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <PreviewSheet booking={preview} timeZone={timeZone} onClose={() => setPreview(null)} onTransition={transition} />
    </div>
  );
}

function statusPalette(status: string) {
  switch (status) {
    case "CONFIRMED": return "border-sky-400 bg-sky-100 text-sky-950";
    case "CHECKED_IN": return "border-amber-400 bg-amber-100 text-amber-950";
    case "IN_PROGRESS": return "border-purple-400 bg-purple-100 text-purple-950";
    case "COMPLETED": return "border-emerald-400 bg-emerald-100 text-emerald-950";
    case "CANCELLED": return "border-rose-300 bg-rose-100/60 text-rose-700 line-through";
    case "NO_SHOW": return "border-rose-400 bg-rose-100 text-rose-950";
    default: return "border-muted-foreground/30 bg-muted text-foreground";
  }
}

function visibleHours(hours: HoursRow[], dates: string[], bookings: BookingRow[], timeZone: string): [number, number] {
  const configured = dates.flatMap((date) => hours.filter((row) => row.dayOfWeek === dayOfWeek(date) && !row.isClosed));
  const bookingTimes = bookings.flatMap((booking) => {
    const start = zonedTime(booking.startAt, timeZone);
    const end = zonedTime(booking.endAt, timeZone);
    return [start.hour * 60 + start.minute, end.hour * 60 + end.minute];
  });
  const starts = configured.map((row) => minutes(row.openTime));
  const ends = configured.map((row) => minutes(row.closeTime));
  if (!configured.length && !bookingTimes.length) return [8, 21];
  const start = Math.max(0, Math.floor(Math.min(...starts, ...bookingTimes) / 60));
  const end = Math.min(24, Math.ceil(Math.max(...ends, ...bookingTimes) / 60));
  return [start, Math.max(start + 1, end)];
}

function isOpen(hours: HoursRow[], date: string, minute: number): boolean {
  if (!hours.length) return minute >= 8 * 60 && minute < 21 * 60;
  return hours.some((row) => row.dayOfWeek === dayOfWeek(date) && !row.isClosed && minute >= minutes(row.openTime) && minute < minutes(row.closeTime));
}

function blackoutRange(blackout: BlackoutRow, date: string, timeZone: string): [number, number] | null {
  const start = zonedTime(blackout.startsAtUtc, timeZone);
  const end = zonedTime(blackout.endsAtUtc, timeZone);
  if (start.localDate > date || end.localDate < date || (end.localDate === date && end.hour === 0 && end.minute === 0)) return null;
  const from = start.localDate < date ? 0 : start.hour * 60 + start.minute;
  const to = end.localDate > date ? 1440 : end.hour * 60 + end.minute;
  return to > from ? [from, to] : null;
}

function DayGrid({ day, bookings, resources, selectedResourceId, hours, blackouts, timeZone, reschedulingId, onOpen, onNew, onReschedule }: {
  day: string; bookings: BookingRow[]; resources: ResourceRow[]; selectedResourceId: string; hours: HoursRow[]; blackouts: BlackoutRow[]; timeZone: string;
  reschedulingId: number | null; onOpen: (booking: BookingRow) => void; onNew: (date: string, time: string, resourceId: number) => void;
  onReschedule: (booking: BookingRow, date: string, time: string, resourceId: number) => Promise<void>;
}) {
  const [clock, setClock] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setClock(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const visibleResources = resources
    .filter((resource) => !selectedResourceId || resource.id === Number(selectedResourceId))
    .sort((a, b) => a.type === b.type ? a.name.localeCompare(b.name) : a.type === "STAFF_MEMBER" ? 1 : -1);
  const todaysBookings = bookings.filter((booking) => zonedTime(booking.startAt, timeZone).localDate === day);
  const [hourStart, hourEnd] = visibleHours(hours, [day], todaysBookings, timeZone);
  const slots = Array.from({ length: (hourEnd - hourStart) * (60 / SLOT_MINUTES) }, (_, index) => hourStart * 60 + index * SLOT_MINUTES);
  const now = zonedTime(clock, timeZone);
  const nowMinute = now.hour * 60 + now.minute;
  const showNow = now.localDate === day && nowMinute >= hourStart * 60 && nowMinute <= hourEnd * 60;

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[760px]" style={{ minWidth: `${Math.max(760, 72 + visibleResources.length * 150)}px` }}>
        <div className="grid" style={{ gridTemplateColumns: `4.5rem repeat(${visibleResources.length}, minmax(150px, 1fr))` }}>
          <div className="sticky top-0 z-20 border-b bg-background" />
          {visibleResources.map((resource) => (
            <div key={resource.id} className="sticky top-0 z-20 border-b bg-background px-3 py-2 text-center">
              <p className="truncate text-sm font-medium">{resource.name}</p>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{resource.type.replaceAll("_", " ")}</p>
            </div>
          ))}
          <div className="relative">
            {slots.map((minute, index) => (
              <div key={minute} className={cn("border-b pr-2 text-right text-[10px] text-muted-foreground", index % 2 ? "border-dashed border-muted-foreground/10" : "border-muted-foreground/20")} style={{ height: SLOT_HEIGHT }}>
                {minute % 60 === 0 ? timeLabel(minute) : ""}
              </div>
            ))}
          </div>
          {visibleResources.map((resource) => {
            const assigned = todaysBookings.filter((booking) => assignmentsOf(booking).some((item) => item.id === resource.id));
            const relevantBlackouts = blackouts.filter((blackout) => blackout.resourceId === null || blackout.resourceId === resource.id);
            return (
              <div key={resource.id} className="relative border-l" style={{ height: slots.length * SLOT_HEIGHT }}>
                {slots.map((minute, index) => {
                  const closed = !isOpen(hours, day, minute);
                  const blocked = relevantBlackouts.some((blackout) => {
                    const range = blackoutRange(blackout, day, timeZone);
                    return range && minute < range[1] && minute + SLOT_MINUTES > range[0];
                  });
                  return (
                    <button
                      key={minute}
                      type="button"
                      aria-label={`Create booking for ${resource.name} at ${timeLabel(minute)}`}
                      className={cn(
                        "absolute left-0 right-0 border-b text-left transition-colors hover:bg-primary/5",
                        index % 2 ? "border-dashed border-muted-foreground/10" : "border-muted-foreground/20",
                        closed && "bg-slate-100/80 [background-image:repeating-linear-gradient(135deg,transparent,transparent_5px,rgba(100,116,139,.07)_5px,rgba(100,116,139,.07)_10px)]",
                        blocked && "bg-rose-100/70 [background-image:repeating-linear-gradient(135deg,transparent,transparent_5px,rgba(244,63,94,.1)_5px,rgba(244,63,94,.1)_10px)]",
                      )}
                      style={{ top: index * SLOT_HEIGHT, height: SLOT_HEIGHT }}
                      disabled={closed || blocked}
                      onClick={() => onNew(day, timeLabel(minute), resource.id)}
                      onDragOver={(event) => { if (!closed && !blocked) { event.preventDefault(); event.dataTransfer.dropEffect = "move"; } }}
                      onDrop={(event) => {
                        if (closed || blocked) return;
                        event.preventDefault();
                        const booking = bookings.find((item) => item.id === Number(event.dataTransfer.getData("text/booking-id")));
                        if (booking) void onReschedule(booking, day, timeLabel(minute), resource.id);
                      }}
                    />
                  );
                })}
                {relevantBlackouts.map((blackout) => {
                  const range = blackoutRange(blackout, day, timeZone);
                  if (!range) return null;
                  const top = (Math.max(range[0], hourStart * 60) - hourStart * 60) / SLOT_MINUTES * SLOT_HEIGHT;
                  const bottom = (Math.min(range[1], hourEnd * 60) - hourStart * 60) / SLOT_MINUTES * SLOT_HEIGHT;
                  if (bottom <= top) return null;
                  return <div key={blackout.id} className="pointer-events-none absolute left-1 right-1 z-[2] overflow-hidden rounded border border-rose-300/70 bg-rose-100/75 px-1.5 py-1 text-[10px] text-rose-800" style={{ top, height: bottom - top }}>{blackout.reason || "Unavailable"}</div>;
                })}
                {assigned.map((booking) => {
                  const start = zonedTime(booking.startAt, timeZone);
                  const duration = (new Date(booking.endAt).getTime() - new Date(booking.startAt).getTime()) / 60_000;
                  const startMinute = start.hour * 60 + start.minute;
                  const top = (startMinute - hourStart * 60) / SLOT_MINUTES * SLOT_HEIGHT;
                  const height = Math.max(20, duration / SLOT_MINUTES * SLOT_HEIGHT);
                  return (
                    <button
                      key={`${booking.id}-${resource.id}`}
                      type="button"
                      draggable={!reschedulingId}
                      onDragStart={(event) => { event.dataTransfer.setData("text/booking-id", String(booking.id)); event.dataTransfer.effectAllowed = "move"; }}
                      onClick={() => onOpen(booking)}
                      className={cn("absolute left-1 right-1 z-10 overflow-hidden rounded-md border border-l-4 px-2 py-1 text-left text-xs shadow-sm transition hover:z-20 hover:shadow-md", statusPalette(booking.status), reschedulingId === booking.id && "opacity-50")}
                      style={{ top, height }}
                    >
                      <p className="truncate font-semibold">{booking.customer.name}</p>
                      <p className="truncate text-[10px] opacity-80">{timeLabel(startMinute)} · {booking.vehicle?.plate || booking.code}</p>
                    </button>
                  );
                })}
                {showNow && <div className="pointer-events-none absolute left-0 right-0 z-30 border-t-2 border-rose-500" style={{ top: (nowMinute - hourStart * 60) / SLOT_MINUTES * SLOT_HEIGHT }}><span className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-rose-500" /></div>}
              </div>
            );
          })}
        </div>
        {!visibleResources.length && <p className="py-10 text-center text-sm text-muted-foreground">No resources match this filter.</p>}
        <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground">
          <span>Click an empty slot to create. Drag a booking to reschedule.</span>
          <span>Times shown in {timeZone}</span>
        </div>
      </div>
    </div>
  );
}

function WeekView({ start, bookings, resources, hours, blackouts, timeZone, reschedulingId, onOpen, onNew, onReschedule }: {
  start: string; bookings: BookingRow[]; resources: ResourceRow[]; hours: HoursRow[]; blackouts: BlackoutRow[]; timeZone: string; reschedulingId: number | null;
  onOpen: (booking: BookingRow) => void; onNew: (date: string, time: string, resourceId?: number) => void;
  onReschedule: (booking: BookingRow, date: string, time: string, resourceId: number) => Promise<void>;
}) {
  const dates = Array.from({ length: 7 }, (_, index) => addDays(start, index));
  const today = todayInZone(timeZone);
  return (
    <div>
      <div className="overflow-x-auto rounded-lg border">
      <div className="grid min-w-[760px] grid-cols-7 overflow-hidden">
        {dates.map((date) => {
          const daily = bookings.filter((booking) => zonedTime(booking.startAt, timeZone).localDate === date);
          const dayHours = hours.filter((row) => row.dayOfWeek === dayOfWeek(date) && !row.isClosed);
          const closed = hours.length > 0 && dayHours.length === 0;
          const dayBlackouts = blackouts.filter((blackout) => blackoutRange(blackout, date, timeZone));
          return (
            <section
              key={date}
              className={cn("min-h-[430px] border-r bg-background last:border-r-0", closed && "bg-slate-50")}
              onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = "move"; }}
              onDrop={(event) => {
                event.preventDefault();
                const booking = bookings.find((item) => item.id === Number(event.dataTransfer.getData("text/booking-id")));
                if (!booking) return;
                const local = zonedTime(booking.startAt, timeZone);
                void onReschedule(booking, date, timeLabel(local.hour * 60 + local.minute), booking.resource.id);
              }}
            >
              <button type="button" onClick={() => onNew(date, dayHours[0]?.openTime?.slice(0, 5) ?? "09:00")} className={cn("w-full border-b px-2 py-3 text-center hover:bg-accent", date === today && "bg-primary/5")}>
                <p className="text-[10px] font-medium uppercase text-muted-foreground">{formatDay(date, { weekday: "short" })}</p>
                <p className={cn("mt-1 text-lg font-semibold", date === today && "text-primary")}>{Number(date.slice(8))}</p>
                <p className="truncate text-[9px] text-muted-foreground">{closed ? "Closed" : dayHours.length ? dayHours.map((row) => `${row.openTime.slice(0, 5)}–${row.closeTime.slice(0, 5)}`).join(", ") : "Hours not set"}</p>
              </button>
              {dayBlackouts.map((blackout) => {
                const resource = resources.find((item) => item.id === blackout.resourceId);
                return <div key={blackout.id} className="m-1 rounded border border-rose-200 bg-rose-50 px-1.5 py-1 text-[10px] text-rose-700">{blackout.reason || "Unavailable"}{resource ? ` · ${resource.name}` : " · all resources"}</div>;
              })}
              <div className="space-y-1.5 p-1.5">
                {daily.map((booking) => {
                  const local = zonedTime(booking.startAt, timeZone);
                  return (
                    <button key={booking.id} type="button" draggable={!reschedulingId} onDragStart={(event) => event.dataTransfer.setData("text/booking-id", String(booking.id))} onClick={() => onOpen(booking)} className={cn("w-full rounded border border-l-4 p-2 text-left text-[11px] shadow-sm", statusPalette(booking.status), reschedulingId === booking.id && "opacity-50")}>
                      <p className="font-semibold tabular-nums">{timeLabel(local.hour * 60 + local.minute)}</p>
                      <p className="truncate">{booking.customer.name}</p>
                      <p className="truncate text-[9px] opacity-75">{assignmentsOf(booking).map((item) => item.name).join(", ")}</p>
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
      </div>
      <p className="mt-3 text-right text-[11px] text-muted-foreground">Drop on another day to keep the local start time · {timeZone}</p>
    </div>
  );
}

function ListView({ bookings, timeZone, onOpen }: { bookings: BookingRow[]; timeZone: string; onOpen: (booking: BookingRow) => void }) {
  if (!bookings.length) return <EmptyState />;
  return (
    <Table>
      <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Status</TableHead><TableHead>Mode</TableHead><TableHead>Customer</TableHead><TableHead>When</TableHead><TableHead>Assignments</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
      <TableBody>
        {bookings.map((booking) => (
          <TableRow key={booking.id} className="cursor-pointer" onClick={() => onOpen(booking)}>
            <TableCell className="font-mono">{booking.code}</TableCell>
            <TableCell><Badge>{booking.status.replaceAll("_", " ")}</Badge></TableCell>
            <TableCell>{modeOf(booking)}</TableCell>
            <TableCell>{booking.customer.name}</TableCell>
            <TableCell>{formatDateTime(booking.startAt, timeZone)}</TableCell>
            <TableCell>{assignmentsOf(booking).map((item) => item.name).join(", ")}</TableCell>
            <TableCell className="text-right tabular-nums">{formatMoney(booking.total.amount, booking.total.currency)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function Agenda({ bookings, timeZone, onOpen }: { bookings: BookingRow[]; timeZone: string; onOpen: (booking: BookingRow) => void }) {
  const grouped = new Map<string, BookingRow[]>();
  for (const booking of bookings) {
    const date = zonedTime(booking.startAt, timeZone).localDate;
    grouped.set(date, [...(grouped.get(date) ?? []), booking]);
  }
  if (!bookings.length) return <EmptyState />;
  return (
    <div className="space-y-5">
      {Array.from(grouped).map(([date, daily]) => (
        <section key={date}>
          <h3 className="mb-2 border-b pb-2 text-sm font-semibold">{formatDay(date, { weekday: "long", month: "long", day: "numeric" })}</h3>
          <div className="space-y-2">
            {daily.map((booking) => {
              const start = zonedTime(booking.startAt, timeZone);
              const end = zonedTime(booking.endAt, timeZone);
              return (
                <button key={booking.id} type="button" onClick={() => onOpen(booking)} className={cn("flex w-full gap-3 rounded-lg border border-l-4 p-3 text-left shadow-sm", statusPalette(booking.status))}>
                  <div className="w-14 shrink-0 font-semibold tabular-nums">{timeLabel(start.hour * 60 + start.minute)}<p className="text-[10px] font-normal opacity-70">to {timeLabel(end.hour * 60 + end.minute)}</p></div>
                  <div className="min-w-0"><p className="truncate font-semibold">{booking.customer.name}</p><p className="truncate text-xs opacity-75">{booking.vehicle?.plate ? `${booking.vehicle.plate} · ` : ""}{assignmentsOf(booking).map((item) => item.name).join(", ")}</p><Badge className="mt-1">{booking.status.replaceAll("_", " ")}</Badge></div>
                </button>
              );
            })}
          </div>
        </section>
      ))}
      <p className="text-right text-[11px] text-muted-foreground">Times shown in {timeZone}</p>
    </div>
  );
}

function EmptyState() {
  return <div className="grid min-h-48 place-items-center rounded-lg border border-dashed text-center"><div><CalendarDays className="mx-auto mb-2 h-6 w-6 text-muted-foreground" /><p className="text-sm font-medium">No bookings in this period</p><p className="text-xs text-muted-foreground">Try changing the date or filters.</p></div></div>;
}

function PreviewSheet({ booking, timeZone, onClose, onTransition }: { booking: BookingRow | null; timeZone: string; onClose: () => void; onTransition: (booking: BookingRow, status: "CHECKED_IN" | "IN_PROGRESS" | "COMPLETED" | "NO_SHOW") => Promise<void> }) {
  const next = booking?.status === "CONFIRMED" ? "CHECKED_IN" : booking?.status === "CHECKED_IN" ? "IN_PROGRESS" : booking?.status === "IN_PROGRESS" ? "COMPLETED" : null;
  return (
    <Sheet open={Boolean(booking)} onClose={onClose} title={booking?.code} description="Booking preview" footer={booking && <Link to={`/bookings/${booking.id}`} className={cn(buttonVariants(), "w-full")}>Open booking details</Link>}>
      {booking && (
        <div className="space-y-5">
          <div className={cn("rounded-lg border border-l-4 p-4", statusPalette(booking.status))}><Badge>{booking.status.replaceAll("_", " ")}</Badge><p className="mt-2 text-lg font-semibold">{formatDateTime(booking.startAt, timeZone)}</p><p className="text-sm opacity-75">to {new Intl.DateTimeFormat(undefined, { timeStyle: "short", timeZone }).format(new Date(booking.endAt))} · {timeZone}</p></div>
          <div className="space-y-4 text-sm">
             <div className="flex gap-3"><UserRound className="mt-0.5 h-4 w-4 text-muted-foreground" /><div><p className="font-medium">{booking.customer.name}</p>{booking.customer.phone && <p className="text-muted-foreground">{booking.customer.phone}</p>}{booking.vehicle && <p className="text-muted-foreground">Vehicle {booking.vehicle.plate}</p>}<Link to={`/customers/${booking.customer.id}`} className="text-xs font-medium text-primary hover:underline">View customer</Link></div></div>
            <div className="flex gap-3"><Wrench className="mt-0.5 h-4 w-4 text-muted-foreground" /><div><p className="font-medium">{modeOf(booking) === "MOBILE" ? "Mobile service" : "Stationary service"}</p>{assignmentsOf(booking).map((resource) => <p key={resource.id} className="text-muted-foreground">{resource.name} · {resource.role.toLowerCase()}</p>)}</div></div>
            <div className="flex gap-3"><MapPin className="mt-0.5 h-4 w-4 text-muted-foreground" /><div><p className="font-medium">{booking.location.name}</p><p className="text-muted-foreground">{timeZone}</p></div></div>
           </div>
          {(next || booking.status === "CONFIRMED" || booking.status === "CHECKED_IN") && <div className="flex flex-wrap gap-2 border-t pt-4">{next && <Button size="sm" onClick={() => void onTransition(booking, next)}>Mark {next.toLowerCase().replaceAll("_", " ")}</Button>}{(booking.status === "CONFIRMED" || booking.status === "CHECKED_IN") && <Button size="sm" variant="outline" onClick={() => void onTransition(booking, "NO_SHOW")}>Mark no-show</Button>}</div>}
        </div>
      )}
    </Sheet>
  );
}
